import { toast } from '../common/Popover';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_NODE_API_ENDPOINT } from '../utils';
import { Meta } from '../interfaces/apiMeta';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

export const supportsAdvCustomAudienceObjective = (objective?: string | null): boolean => {
    return objective === 'OUTCOME_LEADS' || objective === 'OUTCOME_SALES';
};

/**
 * Builds the targeting specification object for the campaign API
 */
export const buildTargetingSpec = (
    useCustomAudiences: boolean,
    customAdSetId: Array<Object> | undefined,
    selectLocations: Array<{
        key: string;
        type?: string;
        name?: string;
        geo_hierarchy_name?: string;
        region_id?: number;
    }>,
    excludedLocations: Array<{
        key: string;
        type?: string;
        name?: string;
        geo_hierarchy_name?: string;
        region_id?: number;
    }>,
    ageRange: [number, number],
    selectedTargeting: Array<{ id: string; name: string; type: string }>,
    selectedPlatforms: string[]
) => {
    // console.log(selectedTargeting);

    return {
        custom_audiences: customAdSetId || null,
        geo_locations: {
            countries: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'country' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'country'
                )
                .map((l) => l.key),
            regions: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'region' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'district' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'region'
                )
                .map((l) => (l)),
            cities: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'city' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'city'
                )
                .map((l) => (l)),
            neighborhoods: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'neighborhood' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'neighborhood'
                )
                .map((l) => (l)),
            subcities: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'subcity' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'subcity'
                )
                .map((l) => (l)),
        },
        excluded_geo_locations: {
            countries: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'country' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'country'
                )
                .map((l) => l.key),
            regions: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'region' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'district' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'region'
                )
                .map((l) => (l)),
            cities: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'city' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'city'
                )
                .map((l) => (l)),
            neighborhoods: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'neighborhood' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'neighborhood'
                )
                .map((l) => (l)),
            subcities: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'subcity' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'subcity'
                )
                .map((l) => (l)),
        },

        age_min: ageRange[0],
        age_max: ageRange[1],
        interests: selectedTargeting.filter((t) => t.type === 'interest').map((t) => ({ id: t.id, name: t.name })),
        behaviors: selectedTargeting.filter((t) => t.type === 'behavior').map((t) => ({ id: t.id, name: t.name })),
        demographics: selectedTargeting.filter(
            (t) => t.type === 'demographic' || t.type === 'demographics'
        ).map((t) => ({ id: t.id, name: t.name })),
        publisher_platforms: selectedPlatforms,
    };
};

/**
 * Builds the targeting specification object for the estimate API
 */
export const buildEstimateTargetingSpec = (
    useCustomAudiences: boolean,
    customAdSetId: Array<Object> | undefined,
    selectLocations: Array<{
        key: string;
        type?: string;
        name?: string;
        geo_hierarchy_name?: string;
        region_id?: number;
    }>,
    excludedLocations: Array<{
        key: string;
        type?: string;
        name?: string;
        geo_hierarchy_name?: string;
        region_id?: number;
    }>,
    ageRange: [number, number],
    selectedTargeting: Array<{ id: string; name: string; type: string }>,
    selectedPlatforms: string[]
) => {
    return {
        custom_audiences: customAdSetId || null,
        geo_locations: {
            countries: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'country' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'country'
                )
                .map((l) => (l.key)),
            regions: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'region' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'district' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'region'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            cities: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'city' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'city'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            neighborhoods: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'neighborhood' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'neighborhood'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            subcities: selectLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'subcity' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'subcity'
                )
                .map((l) => ({ key: l.key, name: l.name })),
        },
        excluded_geo_locations: {
            countries: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'country' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'country'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            regions: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'region' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'district' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'region'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            cities: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'city' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'city'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            neighborhoods: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'neighborhood' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'neighborhood'
                )
                .map((l) => ({ key: l.key, name: l.name })),
            subcities: excludedLocations
                .filter(
                    (l) =>
                        l.type?.toLowerCase() === 'subcity' ||
                        l.geo_hierarchy_name?.toLowerCase() === 'subcity'
                )
                .map((l) => ({ key: l.key, name: l.name })),
        },

        age_min: ageRange[0],
        age_max: ageRange[1],
        interests: selectedTargeting.filter((t) => t.type === 'interest').map((t) => ({ id: t.id, name: t.name })),
        behaviors: selectedTargeting.filter((t) => t.type === 'behavior').map((t) => ({ id: t.id, name: t.name })),
        // demographics: selectedTargeting.filter(
        //     (t) => t.type === 'demographic' || t.type === 'demographics'
        // ).map((t) => ({ id: t.id, name: t.name })),
        publisher_platforms: selectedPlatforms,
    };
};

/**
 * Builds the complete campaign payload for the API
 */
const getImageSourceFields = (metaImageHash?: string | null) => ({
    image_source: metaImageHash ? 'meta' : 's3',
    meta_image_hash: metaImageHash || null,
});

export const buildCampaignPayload = (
    campaignName: string,
    startDate: dayjs.Dayjs | null,
    endDate: dayjs.Dayjs | null,
    selectedObjective: string,
    adSetName: string,
    dailyBudget: number | undefined,
    useSavedAudiences: boolean,
    savedAdSetId: string | null,
    targetingSpec: any,
    ads: any[],
    selectedAdAccountId: string,
    selectedWaba: string | null,
    selectedFbPageId: string,
    orgId: string,
    spendCap?: number,
    specialAdCategories?: string[],
    bidStrategy?: string,
    optimizationGoal?: string,
    adsetSchedule?: any[],
    budgetType?: 'DAILY' | 'LIFETIME',
    lifetimeBudget?: number,
    handleAdvantagePlus?: boolean,
    advCustomAudience?: boolean,
    beneficiary?: string | null,
    instagramAccountId?: string | null,
) => {
    return {
        campaign: {
            name: campaignName,
            start_time: startDate
                ? dayjs(startDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            end_time: endDate
                ? dayjs(endDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            objective: selectedObjective,
            spend_cap: spendCap ? spendCap * 100 : null,
            special_ad_categories: specialAdCategories,
        },
        adset: {
            name: campaignName.slice(0, 4) + "_adset_1_" + dayjs().format('ss'),
            daily_budget: budgetType === 'DAILY' ? (dailyBudget ? dailyBudget * 100 : dailyBudget) : null,
            lifetime_budget: budgetType === 'LIFETIME' ? (lifetimeBudget ? lifetimeBudget * 100 : lifetimeBudget) : null,
            saved_audience_id: null,
            targeting: targetingSpec || null,
            start_time: startDate
                ? dayjs(startDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            end_time: endDate
                ? dayjs(endDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            bid_strategy: bidStrategy,
            optimization_goal: optimizationGoal,
            adset_schedule: budgetType === 'LIFETIME' && adsetSchedule && adsetSchedule.length > 0 ? adsetSchedule : null,
            advantage_plus: handleAdvantagePlus ? 1 : 0,
            adv_custom_audience: advCustomAudience && supportsAdvCustomAudienceObjective(selectedObjective) ? 1 : 0,
            beneficiary: beneficiary || null,
        },
        ads: ads.map((ad) => ({
            creative: {
                name: (ad.adName || campaignName).slice(0, 50) + "_creative_" + dayjs().format('ss'),
                message: ad.primaryText,
                ...(ad.mediaType === 'video'
                    ? {
                        video_id: ad.videoId,
                        thumbnail_image_url: ad.thumbnailUrl,
                        image_source: ad.metaThumbnailImageHash ? 'meta' : 's3',
                        meta_thumbnail_image_hash: ad.metaThumbnailImageHash || null,
                    }
                    : {
                        picture: ad.mediaUrl,
                        ...getImageSourceFields(ad.metaImageHash),
                    }
                ),
                link_name: ad.headline,
                link_description: ad.descriptionText,
                welcome_message: ad.welcomeMessage,
                prefilled_message: ad.prefilledMessage,
            },
            ad: {
                name: ad.adName,
                status: 'PAUSED',
            }
        })),
        ad_account_id: selectedAdAccountId,
        waba_number: selectedWaba,
        fb_page_id: selectedFbPageId,
        org_id: orgId,
        instagram_id: instagramAccountId ?? null,
    };
};

export const buildCampaignEditPayload = (
    campaignName: string,
    endDate: dayjs.Dayjs | null,
    dailyBudget: number | undefined,
    targetingSpec: any,
    selectedAdAccountId: string,
    selectedWaba: string | null,
    selectedFbPageId: string,
    orgId: string,
    bidStrategy?: string,
    optimizationGoal?: string,
    adsetSchedule?: any[],
    budgetType?: 'DAILY' | 'LIFETIME',
    lifetimeBudget?: number,
    handleAdvantagePlus?: boolean,
    advCustomAudience?: boolean,
    beneficiary?: string | null,
    instagramAccountId?: string | null,
) => {
    return {
        campaign: {
            name: campaignName,
            end_time: endDate
                ? dayjs(endDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            spend_cap: null,
            special_ad_categories: ["None"],
        },
        adset: {
            name: campaignName.slice(0, 4) + "_adset_1_" + dayjs().format('ss'),
            daily_budget: budgetType === 'DAILY' ? (dailyBudget ? dailyBudget * 100 : dailyBudget) : null,
            lifetime_budget: budgetType === 'LIFETIME' ? (lifetimeBudget ? lifetimeBudget * 100 : lifetimeBudget) : null,
            saved_audience_id: null,
            targeting: targetingSpec || null,
            end_time: endDate
                ? dayjs(endDate).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            bid_strategy: bidStrategy,
            optimization_goal: optimizationGoal,
            adset_schedule: budgetType === 'LIFETIME' && adsetSchedule && adsetSchedule.length > 0 ? adsetSchedule : null,
            advantage_plus: handleAdvantagePlus ? 1 : 0,
            adv_custom_audience: advCustomAudience ? 1 : 0,
            beneficiary: beneficiary || null,
        },
        ad_account_id: selectedAdAccountId,
        waba_number: selectedWaba,
        fb_page_id: selectedFbPageId,
        org_id: orgId,
        instagram_id: instagramAccountId ?? null,
    };
};
/**
 * Uploads media file to the server
 */
export interface UploadedMedia {
    url: string;
    hash?: string;
    imageSource?: 'meta' | 's3';
}

type SaveMediaResponseData = string | {
    hash?: string;
    imageUrl?: string;
    image_url?: string;
};

export const uploadMediaWithMeta = async (file: File, orgId: string, adAccountId: string): Promise<UploadedMedia> => {
    if (!orgId) {
        toast('error', 'Account not found');
        throw new Error('Account not found');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', file.type);

    if (file.type.startsWith('video/mp4')) {
        formData.append('video_duration', '15');
        const response = await coreAPIService.post<{
            meta: Meta;
            data: { videoId: string };
        }>(CTW_NODE_API_ENDPOINT, `/management/v1/uploadVideoToMeta/${orgId}?ad_account_id=${adAccountId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        // console.log(response);

        if (response.meta.success) {
            return { url: response.data.videoId };
        }
        return { url: '' };
    } else {

        const response = await coreAPIService.post<{
            meta: Meta;
            data: { data: SaveMediaResponseData; success: boolean };
        }>(CTW_NODE_API_ENDPOINT, `/management/v1/save/${orgId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        if (response.data.success) {
            const uploadedData = response.data.data;
            if (typeof uploadedData === 'string') {
                return { url: uploadedData, imageSource: 's3' };
            }

            if (!uploadedData) {
                return { url: '' };
            }

            const url = uploadedData.imageUrl || uploadedData.image_url || '';
            return {
                url,
                hash: uploadedData.hash,
                imageSource: uploadedData.hash ? 'meta' : 's3',
            };
        }
        return { url: '' };
    }
};

export const uploadMedia = async (file: File, orgId: string, adAccountId: string): Promise<string> => {
    const uploadedMedia = await uploadMediaWithMeta(file, orgId, adAccountId);
    return uploadedMedia.url;
};

/**
 * Returns color for location type tags
 */
export const getTagColor = (type: string): string => {
    switch (type) {
        case 'country':
            return 'blue';
        case 'city':
            return 'green';
        case 'region':
            return 'volcano';
        default:
            return 'gray';
    }
};

/**
 * Returns color for targeting type tags
 */
export const getTargetingTagColor = (type: string): string => {
    switch (type) {
        case 'interest':
            return 'geekblue';
        case 'behavior':
            return 'magenta';
        case 'demographic':
            return 'cyan';
        default:
            return 'gray';
    }
};

/**
 * Capitalizes the first letter of a string
 */
export const capitalize = (s: string): string =>
    s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Debounce function for search inputs
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
) {
    let timeout: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: Parameters<T>) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}
